// =============================================
// notesApi — scoped, authenticated CRUD for the Notes tables
// =============================================
// Drop-in replacements for dataAccess.loadTable/addRow/saveRow that talk to the
// per-user /api/kv/table/* endpoints (so EVERY logged-in user persists their
// own notes server-side — the generic /api/db CRUD is admin-only). When the API
// or a token isn't available (pure localStorage / offline mode) these fall back
// to dataAccess, keeping the local-only path working. Returned rows are mirrored
// into the same local cache the rest of the app reads via getTableData().
// (Mirrors src/features/music/musicApi.ts.)
// =============================================
import {
  loadTable as dbLoadTable, addRow as dbAddRow, saveRow as dbSaveRow,
  getTableData, saveTableData, checkApi,
} from '../db-viewer/data/dataAccess';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const NOTE_PK: Record<string, string> = {
  kv_entry: 'note_id',
  kv_category: 'category_id',
  kv_category_link: 'link_id',
  kv_media: 'media_id',
};

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

async function apiReady(): Promise<boolean> {
  return !!authToken() && (await checkApi());
}

function authHeaders(json = false): Record<string, string> {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${authToken()}`,
  };
}

/** A server error (4xx/5xx with a body) — surface it; network errors fall back to local. */
function isNetworkLevel(err: any): boolean {
  return !(err && typeof err.status === 'number');
}

async function asError(res: Response): Promise<Error> {
  const body = await res.json().catch(() => ({} as any));
  const e = new Error(body.message || `Notes API ${res.status}`);
  (e as any).status = res.status;
  return e;
}

/** Load a notes table — scoped to the current user server-side, mirrored to cache. */
export async function loadTable(name: string): Promise<Record<string, any>[]> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/kv/table/${name}`, { headers: authHeaders() });
      if (!res.ok) throw await asError(res);
      const rows = await res.json();
      try { saveTableData(name, rows); } catch { /* quota — best effort */ }
      return rows;
    } catch {
      // Reads fall back to the local cache on ANY error (matches dataAccess.loadTable)
      // so one failing table never blocks the whole notes page.
    }
  }
  return dbLoadTable(name);
}

/** Insert into a notes table — ownership enforced server-side; mirrors to cache. */
export async function addRow(name: string, row: Record<string, any>, pk?: string): Promise<Record<string, any>> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/kv/table/${name}`, {
        method: 'POST', headers: authHeaders(true), body: JSON.stringify(row),
      });
      if (!res.ok) throw await asError(res);
      const saved = await res.json();
      try { const d = getTableData(name); d.push(saved); saveTableData(name, d); } catch { /* best effort */ }
      return saved;
    } catch (err) {
      if (!isNetworkLevel(err)) throw err;
    }
  }
  return dbAddRow(name, row, pk);
}

/** Update a notes table row — ownership enforced server-side; patches the cache. */
export async function saveRow(name: string, id: number | string, updates: Record<string, any>, pk?: string): Promise<Record<string, any>> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/kv/table/${name}/${id}`, {
        method: 'PUT', headers: authHeaders(true), body: JSON.stringify(updates),
      });
      if (!res.ok) throw await asError(res);
      const saved = await res.json();
      try {
        const key = pk || NOTE_PK[name];
        const d = getTableData(name);
        // eslint-disable-next-line eqeqeq
        const i = d.findIndex(r => r[key] == id);
        if (i >= 0) { d[i] = { ...d[i], ...saved }; saveTableData(name, d); }
      } catch { /* best effort */ }
      return saved;
    } catch (err) {
      if (!isNetworkLevel(err)) throw err;
    }
  }
  return dbSaveRow(name, id, updates, pk);
}

/** Soft-delete a row (is_deleted = 1) through the scoped update path. */
export async function removeRow(name: string, id: number | string, pk?: string): Promise<void> {
  await saveRow(name, id, { is_deleted: 1, is_active: 0 }, pk);
}
